import { useId, type ReactNode } from "react";

/**
 * Interruptor de duas posições.
 *
 * É um `button` com `role="switch"`, não uma caixa de seleção nativa: o
 * produto não usa controle cru do navegador (AGENTS §3.1, §14.3). Como botão,
 * já responde a Enter e Espaço e recebe foco na ordem natural.
 *
 * Use quando a mudança vale por si — ligar, silenciar, exibir. Para escolher
 * itens de uma lista que só tem efeito ao enviar um formulário, a semântica
 * correta é outra e não é esta.
 */
export interface SwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: ReactNode;
  /** Linha de apoio abaixo do rótulo. */
  hint?: ReactNode;
  disabled?: boolean;
  /** Motivo da indisponibilidade; fica acessível e vira `title` (§18). */
  disabledReason?: string;
  className?: string;
  id?: string;
}

export function Switch({
  checked,
  onChange,
  label,
  hint,
  disabled = false,
  disabledReason,
  className,
  id,
}: SwitchProps) {
  const generated = useId();
  const baseId = id ?? generated;
  const labelId = `${baseId}-label`;
  const hintId = hint || (disabled && disabledReason) ? `${baseId}-hint` : undefined;

  return (
    <div className={className ? `cx-switch ${className}` : "cx-switch"} data-disabled={disabled ? "true" : undefined}>
      <button
        type="button"
        id={baseId}
        role="switch"
        aria-checked={checked}
        aria-labelledby={labelId}
        aria-describedby={hintId}
        disabled={disabled}
        title={disabled ? disabledReason : undefined}
        className="cx-switch-track"
        onClick={() => onChange(!checked)}
      >
        <span className="cx-switch-knob" aria-hidden="true" />
      </button>
      <span className="cx-switch-copy">
        <span id={labelId} className="cx-switch-label">
          {label}
        </span>
        {(hint || (disabled && disabledReason)) && (
          <span id={hintId} className="cx-switch-hint">
            {hint ?? disabledReason}
          </span>
        )}
      </span>
    </div>
  );
}
